<template>
  <main class="flex fix-dynamic-height flex-col no-copy">
    <router-view :key="$route.path" />
  </main>
  <Toaster :position="'top-right'" :expand="true" class="mt-10" />
</template>

<script setup>
import { onMounted, watch } from "vue";
import { useScriptTag } from "@vueuse/core";
import { use3dMockupStore } from "@/datastore/module/3dmockups.js";
import { useMockupStore } from "@/datastore/module/mockup.js";
import { Toaster } from "@/components/ui/sonner";
import LoginServices from "@/services/signinHelper.js";
import { closeOnboardingModal } from "@/lib/utils";
import { useMetaDataStore } from "@/datastore/module/metaData.js";
import { useRoute } from "vue-router";
import { useVideoMockupsStore } from "@/datastore/module/videoMockup.js";

const noSelectStyle = `
.no-copy {
  -webkit-user-select: none;
  -moz-user-select: none;
  -ms-user-select: none;    
  user-select: none;         
  -webkit-touch-callout: none;
  -khtml-user-select: none;
}
`;

const mockupStore = useMockupStore();
const piniaStore = use3dMockupStore();
const metaDataStore = useMetaDataStore();
const videoStore = useVideoMockupsStore();
const route = useRoute();

onMounted(async () => {
  const style = document.createElement("style");
  style.innerHTML = noSelectStyle;
  document.head.appendChild(style);
  piniaStore.fetchData();
  videoStore.fetchData();
  await mockupStore.initAuth();
});

useScriptTag("https://app.lemonsqueezy.com/js/lemon.js", () => {
  // Init LemonSqueezy
  window.createLemonSqueezy();

  LemonSqueezy.Setup({
    eventHandler: async (eventData) => {
      if (eventData === "close" || eventData.event === "Checkout.Success") {
        closeOnboardingModal.next();
        await mockupStore.initAuth();
      }
    },
  });
});

watch(
  () => mockupStore.isSubscriberLoaded,
  async (newVal) => {
    if (!mockupStore.isSubscriber && newVal) {
      useScriptTag(
        "https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=ca-pub-4986239316862628",
      );
    }
  },
);

watch(
  () => route?.params?.lang,
  (newVal) => {
    if (newVal) metaDataStore.setLocale(newVal);
  },
  { immediate: true },
);

watch(
  () => mockupStore.authUser,
  (newVal) => {
    if (newVal === null) {
      try {
        google.accounts.id.initialize({
          client_id: import.meta.env.VITE_FIREBASE_ONETAP_CLIENTID,
          callback: handleCredentialResponse,
        });
        google.accounts.id.prompt();
      } catch (e) {
        console.error(e);
      }
    }
  },
);

async function handleCredentialResponse(response) {
  mockupStore.resetLoadingState();
  await LoginServices.signinWithCredential(response);
  await mockupStore.initAuth();
}

// This starter template is using Vue 3 experimental <script setup> SFCs
// Check out https://github.com/vuejs/rfcs/blob/script-setup-2/active-rfcs/0000-script-setup.md
</script>

<style>
#app {
  font-family: "Inter", sans-serif;
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
  text-align: center;
  color: rgba(0, 0, 0, 0.87);
}

.fix-dynamic-height {
  min-height: 100dvh;
}
</style>
